import { CLINIC_NAME, CLINIC_SUBTITLE } from './utils/clinicConfig';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { SocketProvider } from './context/SocketContext';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage from './pages/LoginPage';
import NewTokenPage from './pages/NewTokenPage';
import QueuePage from './pages/QueuePage';
import AllPatientsPage from './pages/AllPatientsPage';
import BillingPage from './pages/BillingPage';
import DoctorDashboard from './pages/DoctorDashboard';
import ChangePasswordPage from './pages/ChangePasswordPage';
import LabPage from './pages/LabPage';
import CalendarPage from './pages/CalendarPage';
import PatientTrackerPage from './pages/PatientTrackerPage';
import PharmacyPage from './pages/PharmacyPage';
import TreatmentCatalogPage from './pages/TreatmentCatalogPage';
import BackupPage from './pages/BackupPage';
import ReschedulePage from './pages/ReschedulePage';
import SterilisationPage from './pages/SterilisationPage';
import PatientPortalPage from './pages/PatientPortalPage';

import './styles/global.css';
import './styles/components.css';
import './styles/layout.css';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'doctor' ? '/doctor' : '/receptionist'} replace />;
}

export default function App() {
  React.useEffect(() => {
    document.title = `${CLINIC_NAME} — ${CLINIC_SUBTITLE}`;
  }, []);

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <SocketProvider>
            <BrowserRouter>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<RootRedirect />} />

              {/* Doctor portal - requires authentication + doctor role */}
              <Route
                path="/doctor"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/queue"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <QueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/patients"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <AllPatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/billing"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/settings"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/lab"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <LabPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/calendar"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/tracker"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <PatientTrackerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/pharmacy"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <PharmacyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/treatments"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <TreatmentCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/create-token"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <NewTokenPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/backup"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <BackupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/reschedule"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ReschedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/sterilisation"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <SterilisationPage />
                  </ProtectedRoute>
                }
              />
              {/* Public patient portal — no auth required */}
              <Route path="/portal" element={<PatientPortalPage />} />

              {/* Receptionist portal - requires authentication + receptionist role */}
              <Route
                path="/receptionist"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <NewTokenPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/queue"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <QueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/patients"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <AllPatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/billing"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/settings"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/lab"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <LabPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/calendar"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/tracker"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <PatientTrackerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/pharmacy"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <PharmacyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/treatments"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <TreatmentCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/reschedule"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <ReschedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/sterilisation"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <SterilisationPage />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </SocketProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
